/**
 * The live spatial state — where a player is, how fast, and which way he and his torso
 * are pointing. Recomputed from scratch at the top of every tick, before anything else.
 *
 * Almost every decision the animation system makes reads this and nothing else, so its
 * quirks propagate everywhere. Two are worth knowing before reading the code:
 *
 *   - A player's HEADING comes from his motion when he is moving and from his TORSO when
 *     he is not, so a standing player still faces somewhere sensible.
 *   - His torso angle relative to travel is forced into one of five values, and before
 *     that it is bent to fit combinations the clip library actually contains. Sprinting
 *     nearly sideways does not exist, so the STATE is adjusted rather than the animation.
 *
 * Source: humanoidbase.cpp:1622-1737, `SpatialState` at humanoidbase.hpp:172-194.
 */

import { Quaternion, Vector3 } from 'three';
import {
  absoluteAngle2D,
  angle2D,
  fixAngle,
  normalizedOrZero,
  rotate2D,
  signSide,
  vecLength,
  wrapAngle,
} from '../../core/math';
import { angles as quatAngles, zEuler } from '../../core/quat';
import { forceIntoAllowedBodyDirectionVec } from './quantize';
import { Velocity, floatToEnumVelocity } from './velocity';
import { Foot, sampleRotation, variable, type AnimClip } from './clip';
import { movementAtFrame } from './physics';

const FORWARD = new Vector3(0, -1, 0);

/** The engine's Z Euler extraction. Lives in core/quat.ts; re-exported for its old callers. */
export const quaternionZEuler = zEuler;

/** The full three-angle extraction, for the offline checks that need it. */
export function quaternionAngles(q: Quaternion): { x: number; y: number; z: number } {
  const v = quatAngles(q);
  return { x: v.x, y: v.y, z: v.z };
}

// ---------------------------------------------------------------------------

export interface SpatialState {
  /** World position on the pitch plane. Z is always 0. */
  position: Vector3;
  /** Travel heading, football convention: 0 means travelling along (0,-1,0). */
  angle: number;
  /** Unit form of `angle`. Falls back to the torso heading when standing still. */
  directionVec: Vector3;
  enumVelocity: Velocity;
  floatVelocity: number;

  /** Everything included — physics, every cheat, and being shoved. m/s. */
  actualMovement: Vector3;
  /** `actualMovement` with the visual cheats and half the shoving taken back out. */
  physicsMovement: Vector3;
  /** What the unwarped clip alone would have done this frame, in world space. */
  animMovement: Vector3;
  /** The one everything reads. Always `physicsMovement`, unless a clip declares a stop. */
  movement: Vector3;

  /** Inputs, written by the smuggle code before this runs. */
  actionSmuggleMovement: Vector3;
  movementSmuggleMovement: Vector3;
  positionOffsetMovement: Vector3;

  /** Absolute torso heading, snapped. */
  bodyAngle: number;
  bodyDirectionVec: Vector3;
  /** Torso heading relative to travel, snapped to one of five values. */
  relBodyAngle: number;
  relBodyDirectionVec: Vector3;
  /** The same, after the legality clamp but BEFORE the five-value snap. */
  relBodyAngleNonquantized: number;
  relBodyDirectionVecNonquantized: Vector3;

  foot: Foot;
}

export function createSpatialState(): SpatialState {
  return {
    position: new Vector3(),
    angle: 0,
    directionVec: FORWARD.clone(),
    enumVelocity: Velocity.Idle,
    floatVelocity: 0,
    actualMovement: new Vector3(),
    physicsMovement: new Vector3(),
    animMovement: new Vector3(),
    movement: new Vector3(),
    actionSmuggleMovement: new Vector3(),
    movementSmuggleMovement: new Vector3(),
    positionOffsetMovement: new Vector3(),
    bodyAngle: 0,
    bodyDirectionVec: FORWARD.clone(),
    relBodyAngle: 0,
    relBodyDirectionVec: FORWARD.clone(),
    relBodyAngleNonquantized: 0,
    relBodyDirectionVecNonquantized: FORWARD.clone(),
    foot: Foot.Right,
  };
}

/** What `calculateSpatialState` needs from the humanoid that owns it. */
export interface SpatialInputs {
  clip: AnimClip;
  /** The warped track from the physics solver, in world orientation. */
  positions: Vector3[];
  frameNum: number;
  startPos: Vector3;
  startAngle: number;
  previousPosition2D: Vector3;
  actionSmuggleOffset: Vector3;
  actionSmuggleSustainOffset: Vector3;
  movementSmuggleOffset: Vector3;
  rotationSmuggleOffset: number;
}

const scratchQuat = new Quaternion();

/**
 * Recompute everything. `state` is mutated in place; `previousPosition2D` on the inputs is
 * the caller's to update from `state.position` afterwards.
 *
 * humanoidbase.cpp:1622-1726.
 */
export function calculateSpatialState(state: SpatialState, inputs: SpatialInputs): void {
  const { clip, positions, frameNum } = inputs;

  // ---- 1. position -------------------------------------------------------
  const position = new Vector3();
  if (positions.length > frameNum) {
    position
      .copy(inputs.startPos)
      .add(positions[frameNum]!)
      .add(inputs.actionSmuggleOffset)
      .add(inputs.actionSmuggleSustainOffset)
      .add(inputs.movementSmuggleOffset);
  } else {
    // Defensive only. The solver guarantees a track at least as long as the clip and a
    // clip is always switched at its last frame, so this can fire only if selection
    // failed outright or before the player was ever placed.
    const raw = clip.positionCache[Math.min(frameNum, clip.positionCache.length - 1)];
    position
      .copy(inputs.startPos)
      .add(rotate2D(raw ?? new Vector3(), inputs.startAngle))
      .add(inputs.actionSmuggleOffset)
      .add(inputs.actionSmuggleSustainOffset)
      .add(inputs.movementSmuggleOffset);
  }
  position.z = 0;

  // ---- 2. foot -----------------------------------------------------------
  // Past 120 ms into a clip, the foot it ENDS on is treated as already true. 12 is a bare
  // literal in the original with no derivation given for it.
  if (frameNum > 12) state.foot = clip.outgoingFoot;

  // ---- 3. the movement flavours ------------------------------------------
  state.actualMovement.copy(position).sub(inputs.previousPosition2D).multiplyScalar(100);

  const positionOffsetIgnoreFactor = 0.5;
  state.physicsMovement
    .copy(state.actualMovement)
    .sub(state.actionSmuggleMovement)
    .sub(state.movementSmuggleMovement)
    .addScaledVector(state.positionOffsetMovement, -positionOffsetIgnoreFactor);

  state.animMovement.copy(state.physicsMovement);
  if (positions.length > 0) {
    // Deliberately the ORIGINAL clip's track, not the warped one — so that requeue
    // decisions see what the clip meant to do, with the ball-touch cheating stripped out.
    movementAtFrame(clip.positionCache, frameNum, 1, state.animMovement);
    rotate2D(state.animMovement, inputs.startAngle, state.animMovement);
  }

  state.movement.copy(state.physicsMovement);

  // ---- 4. torso heading, straight off the clip's "body" bone --------------
  const bodyTrack = clip.joints.find((j) => j.name === 'body');
  const bodyZ = bodyTrack
    ? quaternionZEuler(sampleRotation(bodyTrack, clip.frameCount, frameNum, scratchQuat))
    : 0;
  const bodyDirectionVec = rotate2D(
    FORWARD,
    bodyZ + inputs.startAngle + inputs.rotationSmuggleOffset,
  );

  // ---- 5. speed ----------------------------------------------------------
  state.floatVelocity = vecLength(state.movement);
  state.enumVelocity = floatToEnumVelocity(state.floatVelocity);

  // ---- 6. heading --------------------------------------------------------
  if (state.enumVelocity !== Velocity.Idle) {
    normalizedOrZero(state.movement, state.directionVec);
  } else {
    state.directionVec.copy(bodyDirectionVec);
  }

  state.position.copy(position);
  state.angle = wrapAngle(fixAngle(absoluteAngle2D(state.directionVec)));

  // ---- 7. torso relative to travel, bent to fit the clip library ----------
  if (state.enumVelocity !== Velocity.Idle) {
    const adapted = rotate2D(bodyDirectionVec, -state.angle);
    const bodyAngleRel = angle2D(adapted, FORWARD);

    // Certain speed and torso-angle combinations were never animated, so rather than
    // warping a clip to cover them the STATE is pulled back to the nearest legal one.
    // Note both replacements land just INSIDE the threshold they violated, so they
    // immediately re-test as legal. There is no dribbling clause: a dribbler may face
    // any direction he likes.
    if (state.enumVelocity === Velocity.Sprint && Math.abs(bodyAngleRel) >= 0.125 * Math.PI) {
      rotate2D(FORWARD, 0.12 * Math.PI * signSide(bodyAngleRel), adapted);
    } else if (state.enumVelocity === Velocity.Walk && Math.abs(bodyAngleRel) >= 0.5 * Math.PI) {
      rotate2D(FORWARD, 0.495 * Math.PI * signSide(bodyAngleRel), adapted);
    }

    // Order matters: the nonquantized pair carries the clamp above but not the snap below.
    state.relBodyDirectionVecNonquantized.copy(adapted);
    state.relBodyDirectionVec.copy(forceIntoAllowedBodyDirectionVec(adapted));
  } else {
    state.relBodyDirectionVecNonquantized.copy(FORWARD);
    state.relBodyDirectionVec.copy(FORWARD);
  }

  state.relBodyAngle = angle2D(state.relBodyDirectionVec, FORWARD);
  state.relBodyAngleNonquantized = angle2D(state.relBodyDirectionVecNonquantized, FORWARD);
  // The snapped relative direction is rotated back out into world space, which overwrites
  // the raw clip-derived heading computed in step 4.
  rotate2D(state.relBodyDirectionVec, state.angle, state.bodyDirectionVec);
  state.bodyAngle = angle2D(state.bodyDirectionVec, FORWARD);
}

/**
 * The stricter version, run immediately before a selection (humanoidbase.cpp:1728-1737).
 *
 * It takes the clip at its word rather than at what the physics produced: the foot it says
 * you end on is the foot you are on, and if it leaves you on the ground you are declared
 * motionless. Note it does NOT clear the direction, so a player can come out of here
 * standing still while still "facing" along motion that has just been discarded. That is
 * the original's behaviour.
 */
export function calculateFactualSpatialState(state: SpatialState, clip: AnimClip): void {
  state.foot = clip.outgoingFoot;
  if (variable(clip, 'outgoing_special_state') !== '') {
    state.floatVelocity = 0;
    state.enumVelocity = Velocity.Idle;
    state.movement.set(0, 0, 0);
  }
}
